import { getCollection, getInventoryConfig } from "@/api/inventory/getConfig";
import { useUploadMetadataJsonMutation } from "@/api/inventory/metadata";
import type { NFTMetadata } from "@/api/inventory/metadata";
import { createNFTRarityOptions } from "@/constants/nft-rarity";
import { usePersistentForm } from "@/hooks/usePersistForm";
import {
	ActionIcon,
	Button,
	Chip,
	Group,
	NumberInput,
	Select,
	Stack,
	Table,
	Text,
	TextInput,
} from "@mantine/core";
import { IconReload, IconTrash } from "@tabler/icons-react";
import { useStore } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

export const Route = createFileRoute("/_auth/$collection/create-metadata")({
	loader: async ({ params, context }) => {
		const collection = await getCollection({
			queryClient: context.queryClient,
			collectionAddress: params.collection,
		});
		return collection;
	},
	component: CreateMetadataComponent,
});

// Extend NFTMetadata type to include SKU
type ExtendedNFTMetadata = NFTMetadata & {
	sku?: string;
};

interface ColumnVisibility {
	description: boolean;
	certNumber: boolean;
	sku: boolean;
	rarity: boolean;
}

const metadataEntrySchema = z.object({
	tokenId: z.number().min(0, "Token ID must be positive"),
	name: z.string().min(1, "Name is required"),
	displayWeight: z
		.number()
		.min(0, "Display Weight must be positive")
		.default(0),
	description: z.string().optional(),
	certNumber: z.string().optional(),
	sku: z.string().optional(),
	rarity: z.string().optional(),
});

type MetadataEntry = z.infer<typeof metadataEntrySchema>;

const formSchema: z.ZodType = z
	.object({
		entries: z
			.array(metadataEntrySchema)
			.superRefine((entries: MetadataEntry[], ctx) => {
				// Track token IDs and their indices
				const tokenIdMap = new Map<number, number[]>();

				entries.forEach((entry: MetadataEntry, index: number) => {
					const indices = tokenIdMap.get(entry.tokenId) || [];
					indices.push(index);
					tokenIdMap.set(entry.tokenId, indices);
				});

				// Add errors for duplicate token IDs
				tokenIdMap.forEach((indices: number[], _tokenId: number) => {
					if (indices.length > 1) {
						// biome-ignore lint/complexity/noForEach: <explanation>
						indices.forEach((index) => {
							ctx.addIssue({
								code: "custom",
								message: "Token ID must be unique",
								path: [index, "tokenId"],
								fatal: true,
							});
						});
					}
				});
			}),
		columnVisibility: z.object({
			description: z.boolean(),
			certNumber: z.boolean(),
			sku: z.boolean(),
			rarity: z.boolean(),
		}),
	})
	.superRefine((data, ctx) => {
		// Validate required fields based on column visibility
		data.entries.forEach((entry: MetadataEntry, index: number) => {
			if (
				data.columnVisibility.certNumber &&
				(!entry.certNumber || entry.certNumber.trim() === "")
			) {
				ctx.addIssue({
					code: "custom",
					message: "Certificate Number is required when enabled",
					path: ["entries", index, "certNumber"],
					fatal: true,
				});
			}

			if (
				data.columnVisibility.sku &&
				(!entry.sku || entry.sku.trim() === "")
			) {
				ctx.addIssue({
					code: "custom",
					message: "SKU is required when enabled",
					path: ["entries", index, "sku"],
					fatal: true,
				});
			}

			if (data.columnVisibility.rarity && !entry.rarity) {
				ctx.addIssue({
					code: "custom",
					message: "Rarity is required when enabled",
					path: ["entries", index, "rarity"],
					fatal: true,
				});
			}
		});
	});

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
	entries: [
		{
			tokenId: 0,
			name: "",
			displayWeight: 0,
		},
	],
	columnVisibility: {
		description: true,
		certNumber: false,
		sku: false,
		rarity: false,
	},
};

function CreateMetadataComponent() {
	const collection = Route.useLoaderData();

	const config = useQuery(getInventoryConfig());
	const nftRarityOptions = createNFTRarityOptions(config.data?.rarities ?? []);

	const uploadMetadata = useUploadMetadataJsonMutation();

	const form = usePersistentForm(
		`form${window.location.pathname.replace(/\//g, "-")}`,
		{
			defaultValues: DEFAULT_VALUES,
			validators: {
				onSubmit: formSchema,
			},
			onSubmit: async ({ value }) => {
				uploadMetadata.mutate({
					collectionAddress: collection.address,
					metadata: value.entries.map((entry: MetadataEntry) => {
						const metadata: Partial<ExtendedNFTMetadata> = {
							tokenId: entry.tokenId,
							name: entry.name.trim(),
							displayWeight: entry.displayWeight,
						};

						if (
							value.columnVisibility.description &&
							entry.description?.trim()
						) {
							metadata.description = entry.description.trim();
						}

						const attributes = [];
						if (value.columnVisibility.certNumber && entry.certNumber?.trim()) {
							attributes.push({
								trait_type: "Certificate Number",
								value: entry.certNumber.trim(),
							});
						}
						if (value.columnVisibility.sku && entry.sku?.trim()) {
							attributes.push({
								trait_type: "SKU",
								value: entry.sku.trim(),
							});
						}
						if (value.columnVisibility.rarity && entry.rarity) {
							attributes.push({
								trait_type: "Rarity",
								value: entry.rarity,
							});
						}
						metadata.attributes = attributes;

						return metadata as ExtendedNFTMetadata;
					}),
				});
			},
		},
	);

	const isSubmitting = uploadMetadata.isPending;
	const isSubmitted = uploadMetadata.isSuccess;
	const canSubmit = useStore(form.store, (state) => state.canSubmit);
	const entries: MetadataEntry[] = useStore(
		form.store,
		(state) => state.values.entries,
	);
	const columnVisibility = useStore(
		form.store,
		(state) => state.values.columnVisibility,
	);

	const addNewEntry = () => {
		const firstEntry = entries[0];
		const nextTokenId = firstEntry.tokenId + 1;

		// Check if the name follows the pattern "something #tokenId"
		const nameMatch = firstEntry.name.match(/^(.+) #\d+$/);
		const newName = nameMatch
			? `${nameMatch[1]} #${nextTokenId}`
			: firstEntry.name;

		const newEntries = [
			{
				tokenId: nextTokenId,
				name: newName,
				// displayWeight: nextTokenId, // if Weight is required to be same as tokenId,
				displayWeight: firstEntry.displayWeight, // if default weight for all
				description: columnVisibility.description ? firstEntry.description : "",
				certNumber: "",
				sku: "",
				rarity: columnVisibility.rarity ? nftRarityOptions[0].value : undefined,
			},
			...entries,
		];

		// Set the new values and reset the form state
		form.reset({
			entries: newEntries,
			columnVisibility,
		});
	};

	const removeEntry = (index: number) => {
		const newEntries = entries.filter((_: unknown, i: number) => i !== index);
		form.reset({
			entries: newEntries,
			columnVisibility,
		});
	};

	const toggleColumn = (column: keyof ColumnVisibility) => {
		const newColumnVisibility = {
			...columnVisibility,
			[column]: !columnVisibility[column],
		};

		let newEntries = entries;
		// Clear the field values if column is being disabled
		if (columnVisibility[column]) {
			newEntries = entries.map((entry) => ({
				...entry,
				[column]: undefined,
			}));
		} else if (column === "rarity") {
			// Set default rarity when enabling the column
			newEntries = entries.map((entry) => ({
				...entry,
				rarity: nftRarityOptions[0].value,
			}));
		}

		form.reset({
			entries: newEntries,
			columnVisibility: newColumnVisibility,
		});
	};

	return (
		<Stack gap="md" p="md" mx="auto">
			<Group justify="space-between">
				<Text size="xl" fw={700}>
					Create Metadata
				</Text>
				<Group gap="sm">
					{isSubmitted ? (
						<>
							<Button
								variant="subtle"
								leftSection={<IconReload size="1.2rem" />}
								onClick={() => {
									form.reset(DEFAULT_VALUES);
									uploadMetadata.reset();
								}}
							>
								Reset Form
							</Button>
							<Button
								variant="light"
								onClick={() => {
									uploadMetadata.reset();
								}}
							>
								Edit Again
							</Button>
						</>
					) : (
						<>
							<Button
								variant="light"
								onClick={addNewEntry}
								disabled={isSubmitting}
							>
								Add Another NFT
							</Button>
							<Button
								onClick={() => form.handleSubmit()}
								loading={isSubmitting}
								disabled={!canSubmit || isSubmitting}
							>
								Upload Metadata
							</Button>
						</>
					)}
				</Group>
			</Group>

			<Stack gap="xs">
				<Text size="sm" fw={500}>
					Enable Additional Fields:
				</Text>
				<Chip.Group>
					<Group wrap="wrap" gap="xs">
						<Chip
							checked={columnVisibility.description}
							onChange={() => toggleColumn("description")}
							disabled={isSubmitted}
						>
							Description
						</Chip>
						<Chip
							checked={columnVisibility.certNumber}
							onChange={() => toggleColumn("certNumber")}
							disabled={isSubmitted}
						>
							PSA Certificate Number
						</Chip>
						<Chip
							checked={columnVisibility.sku}
							onChange={() => toggleColumn("sku")}
							disabled={isSubmitted}
						>
							SKU
						</Chip>
						<Chip
							checked={columnVisibility.rarity}
							onChange={() => toggleColumn("rarity")}
							disabled={isSubmitted}
						>
							Rarity
						</Chip>
					</Group>
				</Chip.Group>
			</Stack>

			<div style={{ overflowX: "auto", width: "100%" }}>
				<Table style={{ minWidth: "800px" }}>
					<Table.Thead>
						<Table.Tr>
							<Table.Th style={{ minWidth: "100px", width: "120px" }}>
								Token ID
							</Table.Th>
							<Table.Th style={{ minWidth: "200px" }}>Name</Table.Th>
							<Table.Th style={{ minWidth: "150px", width: "150px" }}>
								Display Weight
							</Table.Th>

							{columnVisibility.description && (
								<Table.Th style={{ minWidth: "200px" }}>
									Description (Optional)
								</Table.Th>
							)}
							{columnVisibility.certNumber && (
								<Table.Th style={{ minWidth: "200px" }}>
									PSA Certificate Number
								</Table.Th>
							)}
							{columnVisibility.sku && (
								<Table.Th style={{ minWidth: "200px" }}>SKU</Table.Th>
							)}
							{columnVisibility.rarity && (
								<Table.Th style={{ minWidth: "150px" }}>Rarity</Table.Th>
							)}
							<Table.Th style={{ minWidth: "40px", width: "40px" }} />
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						<form.Subscribe selector={(state) => state.values.entries}>
							{(entries) =>
								entries.map((entry: { tokenId: number }, index: number) => (
									<>
										{/* biome-ignore lint/suspicious/noArrayIndexKey: <explanation> */}
										<Table.Tr key={index}>
											<Table.Td>
												<form.Field name={`entries[${index}].tokenId`}>
													{(field) => (
														<NumberInput
															value={field.state.value}
															onChange={(value) =>
																field.handleChange(Number(value))
															}
															onBlur={field.handleBlur}
															required
															allowDecimal={false}
															min={0}
															placeholder="Token ID"
															step={1}
															thousandSeparator=","
															error={!!field.state.meta.errors[0]}
															disabled={isSubmitting || isSubmitted}
														/>
													)}
												</form.Field>
											</Table.Td>
											<Table.Td>
												<form.Field name={`entries[${index}].name`}>
													{(field) => (
														<TextInput
															value={field.state.value}
															onChange={(e) =>
																field.handleChange(e.target.value)
															}
															onBlur={field.handleBlur}
															placeholder="NFT Name"
															disabled={isSubmitted || isSubmitting}
															error={!!field.state.meta.errors?.[0]}
														/>
													)}
												</form.Field>
											</Table.Td>
											<Table.Td>
												<form.Field name={`entries[${index}].displayWeight`}>
													{(field) => (
														<NumberInput
															value={field.state.value}
															onChange={(value) =>
																field.handleChange(Number(value))
															}
															onBlur={field.handleBlur}
															required
															allowDecimal={false}
															min={0}
															placeholder="Display Order"
															step={1}
															thousandSeparator=","
															error={!!field.state.meta.errors[0]}
															disabled={isSubmitting || isSubmitted}
														/>
													)}
												</form.Field>
											</Table.Td>

											{columnVisibility.description && (
												<Table.Td>
													<form.Field name={`entries[${index}].description`}>
														{(field) => (
															<TextInput
																value={field.state.value}
																onChange={(e) =>
																	field.handleChange(e.target.value)
																}
																onBlur={field.handleBlur}
																placeholder="Description (Optional)"
																disabled={isSubmitted || isSubmitting}
																error={!!field.state.meta.errors?.[0]}
															/>
														)}
													</form.Field>
												</Table.Td>
											)}
											{columnVisibility.certNumber && (
												<Table.Td>
													<form.Field name={`entries[${index}].certNumber`}>
														{(field) => (
															<TextInput
																value={field.state.value}
																onChange={(e) =>
																	field.handleChange(e.target.value)
																}
																onBlur={field.handleBlur}
																placeholder="Certificate Number"
																disabled={isSubmitted || isSubmitting}
																error={!!field.state.meta.errors?.[0]}
															/>
														)}
													</form.Field>
												</Table.Td>
											)}
											{columnVisibility.sku && (
												<Table.Td>
													<form.Field name={`entries[${index}].sku`}>
														{(field) => (
															<TextInput
																value={field.state.value}
																onChange={(e) =>
																	field.handleChange(e.target.value)
																}
																onBlur={field.handleBlur}
																placeholder="SKU"
																disabled={isSubmitted || isSubmitting}
																error={!!field.state.meta.errors?.[0]}
															/>
														)}
													</form.Field>
												</Table.Td>
											)}
											{columnVisibility.rarity && (
												<Table.Td>
													<form.Field name={`entries[${index}].rarity`}>
														{(field) => (
															<Select
																value={
																	field.state.value || nftRarityOptions[0].value
																}
																onChange={(value) =>
																	field.handleChange(value ?? "")
																}
																onBlur={field.handleBlur}
																data={nftRarityOptions}
																disabled={isSubmitted}
																placeholder="Select rarity"
																required
																clearable={false}
																error={!!field.state.meta.errors[0]}
															/>
														)}
													</form.Field>
												</Table.Td>
											)}
											<Table.Td>
												<ActionIcon
													color="red"
													variant="subtle"
													onClick={() => removeEntry(index)}
													disabled={entries.length === 1 || isSubmitted}
												>
													<IconTrash size="1.2rem" />
												</ActionIcon>
											</Table.Td>
										</Table.Tr>
									</>
								))
							}
						</form.Subscribe>
					</Table.Tbody>
				</Table>
			</div>
		</Stack>
	);
}
